"use client"

import { useEffect, useState } from "react"
import { usePathname, useRouter, useSearchParams } from "next/navigation"
import { Locale } from "@/i18n/i18n-config"
import {
  ProjectsAndNavConnectionQuery,
  SkillsConnectionQuery,
  SkillsLabel,
} from "@/tina/__generated__/types"
import { motion } from "framer-motion"

import ProjectItem from "./ProjectItem"

type ToggleSelection = undefined | "client" | "original"

interface Props {
  projectData: ProjectsAndNavConnectionQuery
  locale: Locale
  skillData: SkillsConnectionQuery
  projectsButtonText: string
  noProjectsText: string
}

export default function ProjectList({
  projectData,
  locale,
  skillData,
  projectsButtonText,
  noProjectsText,
}: Props) {
  const [toggle, setToggle] = useState<ToggleSelection>()
  const [searchInput, setSearchInput] = useState("")
  const [skill, setSkill] = useState<SkillsLabel | "skill">("skill")

  const searchParams = useSearchParams()

  useEffect(() => {
    if (searchParams.get("type") === "originals") {
      setToggle("original")
      console.log("setting original")
    }

    //scroll to id in url
    if (searchParams.get("project")) {
      const id = searchParams.get("project")
      const element = document.getElementById(id || "")
      if (element) {
        element.scrollIntoView({
          behavior: "smooth",
          block: "start",
          inline: "nearest",
        })
      }
    }

  }, [searchParams])


  let projects = projectData.projectsConnection.edges
  let skills = skillData.skillsConnection.edges

  const filteredProjects = projects?.filter((edge) => {
    const project = edge?.node
    if (!project) return false

    if (toggle === "original" && !project.isOriginal) return false
    if (toggle === "client" && project.isOriginal) return false
    if (
      skill !== "skill" &&
      project.skills?.every((item) => item?.label?.label?.en !== skill)
    )
      return false
    if (
      searchInput !== "" &&
      project.title?.[locale]
        ?.toLowerCase()
        .indexOf(searchInput.toLowerCase()) === -1 &&
      searchInput !== "" &&
      project.company?.[locale]
        ?.toLowerCase()
        .indexOf(searchInput.toLowerCase()) === -1
    )
      return false

    return true
  })

  return (
    <>
      <div className="mt-10 flex flex-col w-full lg:w-auto sm:items-start gap-6 lg:my-20 lg:flex-row lg:gap-10 items-center">
        {/* Tri Toggle */}
        <div className="flex h-10 items-center gap-3">
          <button
            className={`relative block transition-all hover:scale-110 active:scale-100 ${
              toggle === "original" || !toggle ? "opacity-40" : "opacity-100"
            }  hover:!opacity-100 `}
            onClick={() => setToggle("client")}
            title={"set toggle to client projects"}
          >
            client
          </button>

          <div className="relative h-full">
            <motion.div
              className={`dark:bg-night  absolute left-0 top-0 flex h-full w-[100px] items-center rounded-full border-[2px] border-jet bg-jet px-[5px]`}
              style={{
                justifyContent: toggle
                  ? toggle === "client"
                    ? "start"
                    : "end"
                  : "center",
              }}
            >
              <motion.div
                layout
                className=" relative z-10 h-[27px]  w-[27px] rounded-full"
                style={{
                  backgroundColor: !toggle ? "white" : "#FFF500",
                }}
              ></motion.div>
            </motion.div>
            <div className="relative flex h-10 w-[100px] items-center  justify-between px-2  ">
              <button
                className=" relative h-full w-full transition-transform hover:scale-125 active:scale-100"
                onClick={() => setToggle("client")}
                title={"set toggle to client projects"}
              >
                <span className="absolute  left-0 top-0 h-2 w-2 translate-x-2 translate-y-4 animate-pulse rounded-full bg-signal"></span>
              </button>

              <button
                className="relative flex h-full w-full transition-transform hover:scale-125 active:scale-100"
                onClick={() => setToggle(undefined)}
                title={"unset toggle"}
              >
                <span className="absolute right-0 top-0 h-2 w-2 -translate-x-2.5 translate-y-4 rounded-full bg-[#FF4D4D]"></span>
              </button>

              <button
                className=" relative h-full w-full transition-transform hover:scale-125 active:scale-100"
                onClick={() => setToggle("original")}
                title={"set toggle to original projects"}
              >
                <span className="absolute right-0 top-0 h-2 w-2 -translate-x-2 translate-y-4 animate-pulse rounded-full bg-signal"></span>
              </button>
            </div>
          </div>
          <button
            className={`relative block transition-all hover:scale-110 active:scale-100 ${
              toggle === "client" || !toggle ? "opacity-40" : "opacity-100"
            }  hover:!opacity-100 `}
            onClick={() => setToggle("original")}
            title={"set toggle to original projects"}
          >
            original
          </button>
        </div>
        {/* Search Bar */}
        <div className="flex flex-col gap-4 lg:flex-row">
          <div className="flex h-[40px]">
            <input
              value={searchInput}
              className="rounded-[10px] border-2 border-jet px-5 transition-all placeholder:text-jet hover:scale-110 hover:placeholder:opacity-100 focus:outline-signal  active:scale-100 lg:w-[243px]"
              placeholder={"Search..."}
              onChange={(e) => setSearchInput(e.target.value)}
            ></input>
          </div>

          {/* Select Box */}
          <div className="relative flex h-[40px]">
            <div className=" relative h-full transition-all hover:scale-110 active:scale-100">
              <select
                className=" h-full cursor-pointer  appearance-none rounded-[10px] border-2 border-jet bg-signal pl-5 pr-10 placeholder:text-jet placeholder:opacity-40 hover:placeholder:opacity-100 focus:outline-jet"
                onChange={(e) => setSkill(e.target.value as SkillsLabel)}
              >
                <option value="skill">Skill</option>
                {skills &&
                  skills.map((edge, i) => {
                    const skill = edge?.node

                    if (!skill) {
                      return null
                    }

                    return (
                      <option
                        key={skill.label?.en || i}
                        value={skill.label?.en || ""}
                      >
                        {skill.label?.[locale]}
                      </option>
                    )
                  })}
              </select>
              <svg
                className="absolute right-4 top-1/2 -translate-y-1"
                width="12"
                height="8"
                viewBox="0 0 12 8"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M1.05452 1.00783L5.64867 6.35962L10.7131 1.00783"
                  stroke="black"
                />
              </svg>
            </div>
          </div>
        </div>
      </div>

      <ul className="mt-6 lg:mt-0 flex flex-col gap-4 lg:gap-0">
        {filteredProjects && filteredProjects?.length > 0 ? (
          filteredProjects.map((edge) => {
            const project = edge?.node
            if (!project) return null

            return (
              <li key={project.title?.en} className="mb-10">
                <ProjectItem
                  vidIsWhite={project.vidIsWhite || false}
                  id={project.id}
                  uncollapsed={searchParams.get("project") === project.id}
                  title={project.title?.[locale] || "Project"}
                  description={project.description?.[locale] || "Description"}
                  testimonialImage={project.testimonialImage || ""}
                  testimonial={project.testimonial?.[locale] || ""}
                  isOriginal={project.isOriginal || false}
                  link={project.link || ""}
                  linkText={projectsButtonText || "view project"}
                  company={project.company?.[locale] || ""}
                  video={project.video || ""}
                  skills={
                    project.skills?.map(
                      (item) => item?.label?.label?.[locale]
                    ) || []
                  }
                />
              </li>
            )
          })
        ) : (
          <li>{noProjectsText}</li>
        )}
      </ul>
    </>
  )
}
